import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AIConversationEngine from '../../components/ai/AIConversationEngine';
import { astraAPI } from '../../services/astraApi';
import huggingFaceService from '../../services/huggingFaceService';

/**
 * Voice Interface (VAMCC) - Voice-Activated Market Control Center
 *
 * PRODUCTION VERSION (2025-12-29): Complete AI Avatar Conversation System
 *
 * Features:
 * - Animated AI Avatar with lip-sync
 * - Natural conversation with Hugging Face AI
 * - Voice recognition and synthesis
 * - Portfolio-aware responses
 * - Casual conversation capabilities
 *
 * Research Sources:
 * - HeyGen Real-Time Conversational Avatars
 * - D-ID Chat API
 * - Hugging Face Speech-to-Speech Pipeline
 * - Linly-Talker Digital Avatar System
 */
const VoiceInterfaceVAMCC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [hfConnected, setHfConnected] = useState(false);
  const [hfToken, setHfToken] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    fetchVoiceStatus();
    checkHuggingFaceConnection();
  }, []);

  const fetchVoiceStatus = async () => {
    try {
      const stats = await astraAPI.getVoiceStatus();
      setVoiceStatus({
        status: stats.initialized ? 'active' : 'initializing',
        language: 'en-IN',
        model: stats.stt_engines?.length > 0 ? stats.stt_engines.join(', ') : 'Web Speech API',
        ttsModel: stats.tts_engines?.length > 0 ? stats.tts_engines.join(', ') : 'Browser TTS',
        totalInteractions: stats.total_interactions || 0
      });
    } catch (error) {
      console.error('Error fetching voice status:', error);
      setVoiceStatus({
        status: 'ready',
        language: 'en-IN',
        model: 'Web Speech API',
        ttsModel: 'Browser TTS',
        totalInteractions: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const checkHuggingFaceConnection = async () => {
    const hasToken = huggingFaceService.hasApiToken();
    setHfConnected(hasToken);
    if (hasToken) {
      const result = await huggingFaceService.testConnection();
      setHfConnected(result.success);
    }
  };

  const saveHfToken = () => {
    huggingFaceService.setApiToken(hfToken);
    checkHuggingFaceConnection();
    setShowSettings(false);
    setHfToken('');
  };

  const features = [
    {
      icon: 'MessageCircle',
      title: 'Natural Conversation',
      description: 'Chat naturally about your portfolio, markets, or anything else',
      color: 'text-blue-400'
    },
    {
      icon: 'Mic',
      title: 'Voice Recognition',
      description: 'Speak commands and questions using your microphone',
      color: 'text-green-400'
    },
    {
      icon: 'TrendingUp',
      title: 'Portfolio Insights',
      description: 'Get real-time insights about your holdings and performance',
      color: 'text-purple-400'
    },
    {
      icon: 'Zap',
      title: 'AI-Powered',
      description: 'Powered by advanced AI for intelligent responses',
      color: 'text-yellow-400'
    }
  ];

  const sampleQuestions = [
    "What's my portfolio value?",
    "How are my investments performing?",
    "What's the market sentiment today?",
    "Tell me about my top holdings",
    "What should I consider before trading?",
    "Explain dollar-cost averaging"
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
          <div className="p-6 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Initializing AI Assistant...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="p-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Breadcrumb />
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">ASTRA AI Assistant</h1>
                <p className="text-muted-foreground mt-1">
                  Your intelligent trading companion with voice and chat capabilities
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
                >
                  <Icon name="Settings" size={20} />
                </button>
                <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full ${
                  hfConnected ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${hfConnected ? 'bg-success animate-pulse' : 'bg-warning'}`}></div>
                  <span className="text-sm font-medium">
                    {hfConnected ? 'AI Enhanced' : 'Basic Mode'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Settings Panel */}
          {showSettings && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-6 p-4 bg-card border border-border rounded-lg"
            >
              <h3 className="font-semibold text-foreground mb-3">Hugging Face API Settings</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Connect your Hugging Face Pro account for enhanced AI responses using models like Mixtral and BlenderBot.
              </p>
              <div className="flex items-center space-x-3">
                <input
                  type="password"
                  value={hfToken}
                  onChange={(e) => setHfToken(e.target.value)}
                  placeholder="Enter your Hugging Face API token"
                  className="flex-1 px-4 py-2 rounded-lg bg-muted border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <Button onClick={saveHfToken} variant="primary">
                  Save Token
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Get your token from: huggingface.co/settings/tokens
              </p>
            </motion.div>
          )}

          {/* Main Content */}
          {showConversation ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex justify-center"
            >
              <AIConversationEngine
                onClose={() => setShowConversation(false)}
                isFullscreen={false}
              />
            </motion.div>
          ) : (
            <>
              {/* Hero Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-center mb-12"
              >
                {/* AI Avatar Preview */}
                <div className="relative inline-block mb-8">
                  <div className="w-48 h-48 rounded-full bg-gradient-to-br from-primary via-blue-500 to-purple-600 p-1 mx-auto">
                    <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 mx-auto mb-2 flex items-center justify-center">
                          <Icon name="Bot" size={40} className="text-white" />
                        </div>
                        <span className="text-white font-medium">ASTRA</span>
                      </div>
                    </div>
                  </div>
                  <motion.div
                    className="absolute -inset-4 rounded-full border-2 border-primary/30"
                    animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>

                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Meet Your AI Trading Companion
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                  Have natural conversations about your portfolio, get market insights,
                  or just chat casually. ASTRA is here to help 24/7.
                </p>

                <Button
                  onClick={() => setShowConversation(true)}
                  size="lg"
                  className="px-8 py-4 text-lg"
                >
                  <Icon name="MessageCircle" size={24} className="mr-2" />
                  Start Conversation
                </Button>
              </motion.div>

              {/* Features Grid */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
              >
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-colors"
                  >
                    <Icon name={feature.icon} size={32} className={feature.color + ' mb-4'} />
                    <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </motion.div>
                ))}
              </motion.div>

              {/* Sample Questions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-card border border-border rounded-xl p-6"
              >
                <h3 className="font-semibold text-foreground mb-4">Try Asking...</h3>
                <div className="flex flex-wrap gap-3">
                  {sampleQuestions.map((question) => (
                    <button
                      key={question}
                      onClick={() => setShowConversation(true)}
                      className="px-4 py-2 rounded-full bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors text-sm"
                    >
                      "{question}"
                    </button>
                  ))}
                </div>
              </motion.div>

              {/* System Status */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-6 p-4 bg-muted/30 rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="Mic" size={16} className="text-primary" />
                      <span className="text-sm text-muted-foreground">
                        STT: {voiceStatus.model}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Volume2" size={16} className="text-primary" />
                      <span className="text-sm text-muted-foreground">
                        TTS: {voiceStatus.ttsModel}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {voiceStatus.totalInteractions} total interactions
                    </span>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default VoiceInterfaceVAMCC;
